export type SupportedLanguageCode = "en" | "es";

const SUPPORTED_LANGUAGE_CODES = new Set<SupportedLanguageCode>(["en", "es"]);

const SPANISH_STOPWORDS = new Set([
  "a",
  "al",
  "como",
  "con",
  "cuando",
  "de",
  "del",
  "donde",
  "el",
  "en",
  "es",
  "esta",
  "este",
  "esto",
  "la",
  "las",
  "lo",
  "los",
  "mas",
  "me",
  "mi",
  "para",
  "por",
  "proceso",
  "que",
  "se",
  "si",
  "su",
  "sus",
  "tambien",
  "te",
  "tu",
  "un",
  "una",
  "y",
]);

const ENGLISH_STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "can",
  "do",
  "for",
  "from",
  "how",
  "if",
  "in",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "this",
  "to",
  "when",
  "where",
  "with",
  "you",
  "your",
]);

const TOKEN_REGEX = /[a-z\u00e1\u00e9\u00ed\u00f3\u00fa\u00f1\u00fc]+/gi;
const SPANISH_CHAR_REGEX =
  /[\u00e1\u00e9\u00ed\u00f3\u00fa\u00f1\u00fc\u00bf\u00a1]/i;

export interface LanguageSignalSummary {
  tokenCount: number;
  spanishHits: number;
  englishHits: number;
  hasSpanishChars: boolean;
}

export function normalizeSupportedLanguage(
  value: unknown,
  fallback: SupportedLanguageCode = "en",
): SupportedLanguageCode {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalized = value.trim().toLowerCase().slice(0, 2);
  if (SUPPORTED_LANGUAGE_CODES.has(normalized as SupportedLanguageCode)) {
    return normalized as SupportedLanguageCode;
  }

  return fallback;
}

function getLanguageSignals(text: string) {
  const normalized = text.trim().toLowerCase();
  const tokens = normalized.match(TOKEN_REGEX) || [];

  let spanishHits = 0;
  let englishHits = 0;

  for (const token of tokens) {
    if (SPANISH_STOPWORDS.has(token)) {
      spanishHits += 1;
    }
    if (ENGLISH_STOPWORDS.has(token)) {
      englishHits += 1;
    }
  }

  return {
    normalized,
    tokens,
    spanishHits,
    englishHits,
    hasSpanishChars: SPANISH_CHAR_REGEX.test(normalized),
  };
}

export function getLanguageSignalSummary(
  text: unknown,
): LanguageSignalSummary | null {
  if (typeof text !== "string") {
    return null;
  }

  const normalized = text.trim();
  if (!normalized) {
    return null;
  }

  const signals = getLanguageSignals(normalized);
  return {
    tokenCount: signals.tokens.length,
    spanishHits: signals.spanishHits,
    englishHits: signals.englishHits,
    hasSpanishChars: signals.hasSpanishChars,
  };
}

export function detectLikelyLanguage(
  text: unknown,
): SupportedLanguageCode | null {
  if (typeof text !== "string") {
    return null;
  }

  const { normalized, tokens, spanishHits, englishHits, hasSpanishChars } =
    getLanguageSignals(text);

  if (!normalized) {
    return null;
  }

  if (tokens.length < 3) {
    return hasSpanishChars ? "es" : null;
  }

  if (hasSpanishChars && spanishHits >= 1 && spanishHits >= englishHits) {
    return "es";
  }

  if (spanishHits >= 4 && spanishHits >= englishHits) {
    return "es";
  }

  if (englishHits >= 6 && spanishHits <= 1) {
    return "en";
  }

  if (englishHits >= 4 && englishHits >= spanishHits + 2) {
    return "en";
  }

  if (spanishHits > englishHits) {
    return "es";
  }

  return null;
}

export function isLikelySpanishText(text: unknown): boolean {
  return detectLikelyLanguage(text) === "es";
}
